import { AppLayout, AppLayoutProps, Box, Container, ContentLayout, Flashbar, FlashbarProps, Grid } from '@amzn/awsui-components-core';
import Header from '@amzn/awsui-components-core/header';
import HelpPanel from '@amzn/awsui-components-core/help-panel';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FooterQuote } from './FooterQuote';
import { appLayoutAriaLabels } from 'src/i18n-strings';
import './DFPHomePage.scss';
import { AppNavigationState, LoadingStatus } from '../context/AppContextModels';
import { useAppContext } from '../context/AppContextProvider';
import { WelcomeContentEntity } from '../das-finsuite/admin-components/AdminModels';
import { ContactsWidgetContent } from '../das-finsuite/widgets/contacts-widget/ContactsWidget';
import { FinanceCalendarWidget, FinanceCalendarWidgetContent } from '../das-finsuite/widgets/finance-calendar-widget/FinanceCalendarWidget';
import { toFinanceCalendarWidgetData } from '../das-finsuite/widgets/finance-calendar-widget/FinanceCalendarData';
import TestimonialsCarousel from '../das-finsuite/widgets/testimonials-widget/TestimonialsCarousel';
import { WelcomeWidget } from '../das-finsuite/widgets/welcome-widget/WelcomeWidget';
import { WelcomePageSection } from './DFPConstants';
import { DFPHomePageSideNavigation } from './DFPHomePageSideNavigation';

export const DFPHomePage: React.FC = () => {
  const location = useLocation();
  const navigationStateMessage = location.state as AppNavigationState;

  const [toolsOpen, setToolsOpen] = useState(false);
  const appLayoutRef = useRef<AppLayoutProps.Ref>(null);
  const appContext = useAppContext();
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const [dfpHomePageWelcomeContent, setDFPHomePageWelcomeContent] = useState<WelcomeContentEntity>();
  const [highlightCalendarEvent, setHighlightCalendarEvent] = useState<{ scenario: string; event: string } | null>(null);

  // Handle navigation state message and clear state after displaying
  useEffect(() => {
    const handleNavigationMessage = () => {
      if (!navigationStateMessage?.message) return;

      setFlashbarItems([
        {
          content: navigationStateMessage.message.content,
          dismissible: true,
          dismissLabel: 'Dismiss message',
          id: '1',
          onDismiss: () => setFlashbarItems([]),
          type: navigationStateMessage.message.type
        }
      ]);

      window.history.replaceState({}, document.title);
    };

    handleNavigationMessage();
  }, [navigationStateMessage]);

  // Handle highlight welcome item from global search
  useEffect(() => {
    if (!navigationStateMessage?.highlightWelcomeItem) return;
    const itemId = navigationStateMessage.highlightWelcomeItem;
    window.history.replaceState({}, document.title);

    // Delay to allow DOM to render
    const timer = setTimeout(() => {
      const el = document.getElementById(`welcome-item-${itemId.replace('welcome-', '')}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('welcome-content-item--highlight');
      setTimeout(() => el.classList.remove('welcome-content-item--highlight'), 2500);
    }, 300);
    return () => clearTimeout(timer);
  }, [navigationStateMessage]);

  // Handle highlight calendar event from global search
  useEffect(() => {
    if (!navigationStateMessage?.highlightCalendarEvent) return;
    window.history.replaceState({}, document.title);
    const timer = setTimeout(() => {
      setHighlightCalendarEvent(navigationStateMessage.highlightCalendarEvent!);
      // Delay scroll to after tab switch and highlight render
      setTimeout(() => {
        const highlighted = document.querySelector('.finance-calendar-event--highlight');
        if (highlighted) {
          highlighted.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          document.getElementById('finance-calendar-widget')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }, 300);
    return () => clearTimeout(timer);
  }, [navigationStateMessage]);

  // Handle context loading error state
  useEffect(() => {
    const handleContextError = () => {
      if (appContext.contextLoadingStatus !== LoadingStatus.Failed) return;

      setFlashbarItems([
        {
          id: 'context_loading_error',
          header: 'Error loading context',
          type: 'error',
          content: appContext.contextLoadingError,
          dismissible: false
        }
      ]);
    };

    handleContextError();
  }, [appContext.contextLoadingStatus]);

  useEffect(() => {
    if (appContext.contextLoadingStatus === LoadingStatus.Completed) {
      setDFPHomePageWelcomeContent({
        welcomeTitle: appContext.homePageContent.welcomeTitle,
        welcomeContentDetails: appContext.homePageContent.welcomeContentDetails
      });
    }
  }, [appContext, appContext.contextLoadingStatus]);

  const welcomeWidget = WelcomeWidget({
    welcomeTitle: dfpHomePageWelcomeContent?.welcomeTitle || '',
    description: '',
    welcomeContentDetails: dfpHomePageWelcomeContent?.welcomeContentDetails || []
  });

  const financeCalendarStatus =
    appContext.contextLoadingStatus === LoadingStatus.Completed
      ? 'finished'
      : appContext.contextLoadingStatus === LoadingStatus.Failed
      ? 'error'
      : 'loading';

  const financeCalendarData = React.useMemo(() => {
    const events = appContext.homePageContent?.financeCalendarEvents || [];
    return toFinanceCalendarWidgetData(events);
  }, [appContext.homePageContent?.financeCalendarEvents]);

  const financeCalendarWidget = FinanceCalendarWidget({
    financeCalendarTitle: "What's Due This Week",
    description: '',
    tableContents: { hasData: financeCalendarStatus, data: financeCalendarData }
  });

  return (
    <>
      <AppLayout
        ref={appLayoutRef}
        headerSelector="#h"
        contentType="dashboard"
        ariaLabels={appLayoutAriaLabels}
        tools={
          <HelpPanel
            header={
              <div style={{ position: 'relative', left: '-5px', whiteSpace: 'nowrap' }}>
                <Box variant="h2">
                  <FontAwesomeIcon icon={faUserPlus} /> Connect with Us
                </Box>
              </div>
            }
          >
            <ContactsWidgetContent
              officeHoursText={appContext.homePageContent?.officeHoursText}
              contacts={appContext.homePageContent?.contactsWidgetContacts ?? []}
            />
          </HelpPanel>
        }
        toolsWidth={235}
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        navigation={<DFPHomePageSideNavigation />}
        stickyNotifications
        notifications={<Flashbar items={flashbarItems} />}
        maxContentWidth={Number.MAX_VALUE}
        content={
          <div className="app-layout-body dfp-home-page__body">
            <ContentLayout>
              <SpaceBetween size="l">
                {/* Row 1: All Your Tools, One Place */}
                <Container header={<Header variant="h2">{WelcomePageSection.welcomeContainerHeader}</Header>}>
                  <Box variant="p" color="text-body-secondary">
                    {WelcomePageSection.welcomeContainerContent}
                  </Box>
                </Container>
                {/* Row 2 */}
                <Grid gridDefinition={[{ colspan: { default: 12, s: 4 } }, { colspan: { default: 12, s: 8 } }]}>
                  {/* Col 1: Quick Links  */}
                  <Container fitHeight header={welcomeWidget.data.header}>
                    <div className="dfp-home-page__welcome-content">{welcomeWidget.data.content}</div>
                  </Container>
                  {/* Col 2: Whast due this week  */}
                  <div id="finance-calendar-widget">
                    <Container fitHeight header={financeCalendarWidget.data.header}>
                      <div className="dfp-home-page__finance-calendar-content">
                        <FinanceCalendarWidgetContent
                          hasData={financeCalendarStatus}
                          data={financeCalendarData}
                          highlightEvent={highlightCalendarEvent}
                        />
                      </div>
                    </Container>
                  </div>
                </Grid>
                {/*  Row 3: Customer Testimonials */}
                <div className="dfp-home-page__testimonials">
                  <TestimonialsCarousel />
                </div>
              </SpaceBetween>
            </ContentLayout>
            {/*  Row 4: Footer Quote */}
            <FooterQuote />
          </div>
        }
      />
    </>
  );
};
